import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useSnackbar } from "notistack";
import { useWhatsappTemplates, useSendBulkWhatsappMessage, useOptInInfluencersWhatsapp, useCustomDeliverables } from "../../hooks/usePlanSummary";
import { useWhatsappNumbers } from "../../hooks/useWhatsapp";
import { countBodyVariables, bodyPreview, isSendableTemplate } from "../../utils/whatsappTemplate";
import DeliverablePickerButton from "./DeliverablePickerButton";

// Same 500-per-request cap the backend enforces (validateBulkMessageInfluencers) — chunk
// so a selection bigger than that doesn't just 400 outright, same pattern as bulk-remove.
const CHUNK_SIZE = 500;

// Same shape the backend falls back to itself when campaignName is omitted (routes/plans.js)
// — used here just to pre-fill the field with something sensible, editable before sending.
function defaultCampaignName(planName) {
  const today = new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  return planName ? `${planName} — ${today}` : "";
}

function WhatsAppBulkSendModal({ open, onClose, planId, planName, selectedIds }) {
  const { enqueueSnackbar } = useSnackbar();
  const { data: templatesData, isLoading: templatesLoading } = useWhatsappTemplates("APPROVED");
  const { data: numbersData } = useWhatsappNumbers();
  const { data: customDeliverables } = useCustomDeliverables();
  const sendMutation = useSendBulkWhatsappMessage(planId);
  const optInMutation = useOptInInfluencersWhatsapp(planId);

  const [wabaId, setWabaId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [variables, setVariables] = useState([]);
  const [campaignName, setCampaignName] = useState("");
  const [optInConfirmed, setOptInConfirmed] = useState(false);
  const [result, setResult] = useState(null);
  const [sending, setSending] = useState(false);

  // Pre-filled once when the dialog opens (not on every render) — a placeholder starting
  // point the sender can edit or clear, matching how the Campaigns page's own "New Campaign"
  // dialog always starts blank but this one has an obvious sensible default to offer instead.
  useEffect(() => {
    if (open) setCampaignName(defaultCampaignName(planName));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const numbers = (numbersData?.tokens || []).filter((n) => n.isActive);
  const selectedNumber = numbers.find((n) => n.wabaId === wabaId) || null;
  const recipientCount = selectedIds.size;
  // Meta's messaging-limit tiers count DISTINCT customers reached outside a customer
  // service window in a rolling 24h, not total messages — so headroom is limit minus
  // however many unique people this number has already reached, not minus messages sent.
  const wouldExceedLimit =
    selectedNumber && recipientCount > selectedNumber.messagingLimit - selectedNumber.recentUniqueRecipients;
  const allTemplates = templatesData?.templates || [];
  // A template belongs to one number's WABA — picking the number first is what actually
  // answers "which number does this send go out from", since a template's own name
  // doesn't say (two numbers can both have a "hello_world").
  const templates = (wabaId ? allTemplates.filter((t) => t.wabaId === wabaId) : allTemplates).filter(isSendableTemplate);
  const selectedTemplate = useMemo(
    () => templates.find((t) => t._id === templateId) || null,
    [templates, templateId]
  );
  const variableCount = useMemo(() => countBodyVariables(selectedTemplate), [selectedTemplate]);

  // Default to the first number once the list loads, so a single-number setup (the
  // common case) needs no extra click.
  useEffect(() => {
    if (!wabaId && numbers.length > 0) setWabaId(numbers[0].wabaId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numbers.length]);

  const handleSelectNumber = (id) => {
    setWabaId(id);
    setTemplateId("");
    setVariables([]);
    setOptInConfirmed(false);
  };

  const handleSelectTemplate = (id) => {
    setTemplateId(id);
    const template = templates.find((t) => t._id === id);
    setVariables(new Array(countBodyVariables(template)).fill(""));
    setResult(null);
  };

  // Shared by the field's own onChange and the deliverables picker's onInsert below —
  // one variable slot, set from either a typed keystroke or a picked-and-joined string,
  // indistinguishable to everything downstream (the submit payload, the preview).
  const setVariableAt = (i, value) => {
    setVariables((prev) => {
      const next = [...prev];
      next[i] = value;
      return next;
    });
  };

  const handleClose = () => {
    if (sending) return;
    setTemplateId("");
    setVariables([]);
    setCampaignName("");
    setOptInConfirmed(false);
    setResult(null);
    onClose();
  };

  const handleConfirmOptIn = async (e) => {
    const checked = e.target.checked;
    setOptInConfirmed(checked);
    if (!checked) return; // unchecking doesn't retract already-recorded consent
    try {
      await optInMutation.mutateAsync(Array.from(selectedIds));
    } catch (error) {
      setOptInConfirmed(false);
      enqueueSnackbar(error?.response?.data?.message || "Failed to record opt-in", { variant: "error" });
    }
  };

  const handleSend = async () => {
    const ids = Array.from(selectedIds);
    if (!templateId || !ids.length) return;

    const chunks = [];
    for (let i = 0; i < ids.length; i += CHUNK_SIZE) chunks.push(ids.slice(i, i + CHUNK_SIZE));

    setSending(true);
    try {
      const results = await Promise.all(
        chunks.map((chunk) => sendMutation.mutateAsync({ influencerIds: chunk, templateId, variables, campaignName: campaignName.trim() }))
      );
      const sent = results.flatMap((r) => r.sent || []);
      const failed = results.flatMap((r) => r.failed || []);
      setResult({ sent, failed });
      if (failed.length === 0) {
        enqueueSnackbar(`Sent to ${sent.length} influencer${sent.length === 1 ? "" : "s"}`, { variant: "success" });
      }
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Failed to send messages", { variant: "error" });
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
        <WhatsAppIcon sx={{ color: "#25D366" }} />
        Send WhatsApp message to {recipientCount} influencer{recipientCount === 1 ? "" : "s"}
      </DialogTitle>
      <DialogContent>
        {templatesLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : numbers.length === 0 ? (
          <Alert severity="info" sx={{ mt: 1 }}>
            No WhatsApp number registered yet.
          </Alert>
        ) : !result ? (
          <>
            <TextField
              fullWidth
              size="small"
              label="Campaign name"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              helperText="Tracked on the WhatsApp Campaigns page — pre-filled, but edit or clear it for your own name."
              sx={{ mt: 1, mb: 2 }}
            />

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="wa-number-label">WhatsApp Number</InputLabel>
              <Select
                labelId="wa-number-label"
                label="WhatsApp Number"
                value={wabaId}
                onChange={(e) => handleSelectNumber(e.target.value)}
              >
                {numbers.map((n) => (
                  <MenuItem key={n.wabaId} value={n.wabaId}>
                    {n.displayPhoneNumber}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedNumber && (
              <Typography sx={{ fontSize: 11.5, color: wouldExceedLimit ? "#C0392B" : "#9CA3AF", mb: 2, mt: -1 }}>
                {selectedNumber.recentUniqueRecipients}/{selectedNumber.messagingLimit} unique recipients reached on this
                number in the last 24h
                {wouldExceedLimit && ` — sending to ${recipientCount} more would exceed the remaining headroom; the excess will be skipped, not silently over-sent`}
              </Typography>
            )}

            {templates.length === 0 ? (
              <Alert severity="info">
                No approved WhatsApp templates for this number yet. WhatsApp only allows
                messaging someone who hasn't texted us first via a pre-approved template —
                create one on the Templates page and wait for Meta's review (usually
                minutes, sometimes up to 24h) before it shows up here.
              </Alert>
            ) : (
              <FormControl fullWidth size="small">
                <InputLabel id="wa-template-label">Template</InputLabel>
                <Select
                  labelId="wa-template-label"
                  label="Template"
                  value={templateId}
                  onChange={(e) => handleSelectTemplate(e.target.value)}
                >
                  {templates.map((t) => (
                    <MenuItem key={t._id} value={t._id}>
                      {t.name} ({t.language})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedTemplate && (
              <Box sx={{ mt: 2 }}>
                {Array.from({ length: variableCount }).map((_, i) => {
                  // Only the one variable the template itself designates (set once in the
                  // Template Builder — see deliverablesVariableIndex) shows the picker, not
                  // every field. Clamped against variableCount too, defending against a
                  // stale value rather than trusting it blindly.
                  const isDeliverablesVariable =
                    selectedTemplate.deliverablesVariableIndex >= 1 &&
                    selectedTemplate.deliverablesVariableIndex <= variableCount &&
                    i + 1 === selectedTemplate.deliverablesVariableIndex;
                  return (
                    <TextField
                      key={i}
                      fullWidth
                      size="small"
                      label={`Variable {{${i + 1}}}`}
                      value={variables[i] || ""}
                      onChange={(e) => setVariableAt(i, e.target.value)}
                      sx={{ mb: 1.5 }}
                      InputProps={
                        isDeliverablesVariable
                          ? {
                              endAdornment: (
                                <DeliverablePickerButton
                                  deliverables={customDeliverables || []}
                                  onInsert={(joined) => setVariableAt(i, joined)}
                                />
                              ),
                            }
                          : undefined
                      }
                    />
                  );
                })}

                <Divider sx={{ my: 1.5 }} />
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.5, mb: 0.5 }}>
                  Preview
                </Typography>
                <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: "#F3FDF6", border: "1px solid #C8F0D4", fontSize: 13.5, mb: 1.5 }}>
                  {bodyPreview(selectedTemplate, variables) || "—"}
                </Box>

                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={optInConfirmed}
                      disabled={optInMutation.isPending}
                      onChange={handleConfirmOptIn}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 12.5, color: "#6B7280" }}>
                      I confirm these {recipientCount} creator{recipientCount === 1 ? "" : "s"} have opted in to
                      receive WhatsApp messages from us — checking this records consent for their number{recipientCount === 1 ? "" : "s"}
                      ; without it the send will be blocked. Messaging without real consent drives up block rate,
                      which is what actually risks this number's quality rating and messaging limit.
                    </Typography>
                  }
                />
                {optInMutation.isPending && (
                  <Typography sx={{ fontSize: 11.5, color: "#9CA3AF", mt: 0.5 }}>Recording consent…</Typography>
                )}
              </Box>
            )}
          </>
        ) : (
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {result.sent.length > 0 && (
              <Alert severity="success">
                Sent to {result.sent.length} influencer{result.sent.length === 1 ? "" : "s"}.
              </Alert>
            )}
            {result.failed.length > 0 && (
              <Alert severity="warning">
                {result.failed.length} failed:
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {result.failed.map((f) => (
                    <li key={f.influencerId}>
                      {f.name} — {f.reason}
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
            <Typography sx={{ fontSize: 12.5, color: "#6B7280" }}>
              This send is tracked as a WhatsApp Campaign — see delivered/read/replied status per
              creator on the <a href="/whatsapp/campaigns" target="_blank" rel="noreferrer">WhatsApp Campaigns</a> page.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} sx={{ textTransform: "none", borderRadius: "10px" }} disabled={sending}>
          {result ? "Close" : "Cancel"}
        </Button>
        {!result && templates.length > 0 && (
          <Button
            onClick={handleSend}
            variant="contained"
            disabled={!templateId || !optInConfirmed || sending}
            sx={{ textTransform: "none", borderRadius: "10px", boxShadow: "none", bgcolor: "#25D366", "&:hover": { bgcolor: "#1FAF57" } }}
          >
            {sending ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Send"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default WhatsAppBulkSendModal;
